import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import Loading from "./Loading";
import { Match } from "../models/Match";
import { getLatestMatches } from "../services/MatchService";
import MatchTable from "./MatchTabel";
import { LiveMatch } from "../services/LiveService";
import LiveMatchGrid from "../pages/LivePage/LiveMatchGrid";

// Create a new mapping for simplified division selection
const simpleDivisionMappings = new Map([
    ["ligaen", "Ligaen"],
    ["1div", "1 Division"],
    ["2div", "2 Division"],
    ["3div", "3 Division"],
    ["4div", "4 Division"],
    ["5div", "5 Division"]
]);

interface RecentMatchesProps {
    compact: boolean;
    /** Maps that finished in the live feed. Each shows above the results until its parsed match
     *  has loaded here, so a result appears the moment the map ends instead of after the parse. */
    pending?: LiveMatch[];
}

// "3divB" → "3div", matching the division filter below.
const simplifyDivision = (division: string) => division.match(/^(\d+div)/)?.[1] || division;

const RecentMatches: React.FC<RecentMatchesProps> = ({ compact, pending }) => {
    const { t } = useTranslation(['pages', 'common']);
    const [matchesByDate, setMatchesByDate] = useState<{ [date: string]: Match[] }>({});
    const [matchesByDivision, setMatchesByDivision] = useState<{ [division: string]: Match[] }>({});
    const [selectedDivision, setSelectedDivision] = useState<string>("all");
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Refs keep fetchData identity stable so InfiniteScroll doesn't re-fire on every state change.
    const pageRef = useRef(0);
    const loadingRef = useRef(false);

    // Keep track of window resizing to handle mobile layout changes
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        try {
            const newMatches = await getLatestMatches(pageRef.current);
            if (!newMatches || newMatches.length === 0) {
                setHasMore(false);
                return;
            }

            setMatchesByDate(prev => {
                const updated = { ...prev };
                newMatches.forEach(match => {
                    const matchDate = match.match_time.split('T')[0];
                    updated[matchDate] = updated[matchDate] || [];
                    if (!updated[matchDate].find(m => m.matchid === match.matchid)) {
                        updated[matchDate].push(match);
                    }
                });
                return updated;
            });

            setMatchesByDivision(prev => {
                const updated = { ...prev };
                newMatches.forEach(match => {
                    const divisions = new Set<string>();
                    divisions.add(match.team1.division);
                    divisions.add(match.team2.division);
                    if (match.team1.playsInLigaen || match.team2.playsInLigaen) {
                        divisions.add("ligaen");
                    }
                    divisions.forEach(division => {
                        const simplifiedDivision = division.match(/^(\d+div)/)?.[1] || division;
                        updated[simplifiedDivision] = updated[simplifiedDivision] || [];
                        if (!updated[simplifiedDivision].find(m => m.matchid === match.matchid)) {
                            updated[simplifiedDivision].push(match);
                        }
                    });
                });
                return updated;
            });

            pageRef.current += 1;
        } catch (error) {
            console.error('Error fetching data:', error);
            setError(true);
        } finally {
            loadingRef.current = false;
        }
    }, []);

    const justFinished = useMemo(() => {
        if (!pending || pending.length === 0) return [];
        const parsed = new Set(Object.values(matchesByDate).flat().map(m => m.matchid));
        return pending.filter(m => !parsed.has(m.matchId)
            && (selectedDivision === "all" || simplifyDivision(m.division) === selectedDivision));
    }, [pending, matchesByDate, selectedDivision]);

    return (
        <div>
            <div className="text-center mb-1">
                <div className={`dropdown d-inline-block ${isMobile ? 'w-100' : ''}`}>
                    <button 
                        className={`btn btn-dropdown dropdown-toggle ${isMobile ? 'w-100' : ''}`} 
                        type="button" 
                        data-bs-toggle="dropdown" 
                        aria-expanded="false"
                        style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(255, 102, 0, 0.2)',
                            borderRadius: '6px',
                            padding: '8px 16px',
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
                        }}
                    >
                        {selectedDivision === "all" ? t('matches.allDivisions') : simpleDivisionMappings.get(selectedDivision)}
                    </button>
                    <ul className={`dropdown-menu ${isMobile ? 'w-100' : ''}`}
                        style={{
                            background: 'rgba(26, 26, 26, 0.95)',
                            border: '1px solid rgba(255, 102, 0, 0.2)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            backdropFilter: 'blur(10px)'
                        }}>
                        <li>
                            <button className="dropdown-item" 
                                onClick={() => setSelectedDivision("all")}
                                style={{
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                                    e.currentTarget.style.color = '#ff9933';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                                }}>
                                {t('matches.allDivisions')}
                            </button>
                        </li>
                        {
                            Array.from(simpleDivisionMappings.keys()).map((division, index) => (
                                <li key={index}>
                                    <button className="dropdown-item" 
                                        onClick={() => setSelectedDivision(division)}
                                        style={{
                                            color: 'rgba(255, 255, 255, 0.8)',
                                            transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(255, 102, 0, 0.1)';
                                            e.currentTarget.style.color = '#ff9933';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                                        }}>
                                        {simpleDivisionMappings.get(division)}
                                    </button>
                                </li>
                            ))
                        }
                    </ul>
                </div>
            </div>
            {justFinished.length > 0 && (
                <div className="text-center">
                    <h2 className={isMobile ? 'fs-4 mt-3 mb-2' : 'mt-4 mb-3'}>{t('live.justFinished')}</h2>
                    <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'left' }}>
                        <LiveMatchGrid matches={justFinished} />
                    </div>
                </div>
            )}
            {
                error ? <div className="text-center">{t('common:errors.fetchData')}</div> : (
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={fetchData}
                        hasMore={hasMore}
                        loader={<Loading key={0} />}
                    >
                        {
                            Object.keys(matchesByDate).map((date, index) => {
                                const matchesForDate = matchesByDate[date];
                                const filteredMatches = selectedDivision === "all" ? matchesForDate :
                                    matchesByDivision[selectedDivision]?.filter(match => match.match_time.split('T')[0] === date) || [];
                                if (filteredMatches.length === 0) return null;
                                return (
                                    <div className="text-center" key={index}>
                                        <h2 className={isMobile ? 'fs-4 mt-3 mb-2' : 'mt-4 mb-3'}>{date}</h2>
                                        <div className="d-flex justify-content-center">
                                            <div style={{ width: '100%', maxWidth: '1200px' }}>
                                                <MatchTable
                                                    matches={filteredMatches}
                                                    compact={isMobile || compact}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </InfiniteScroll>
                )
            }
        </div>
    );
};

export default RecentMatches;